import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

export default function DPTerritoryApprovalsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [rejectNote, setRejectNote] = useState({});
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canApprove = perms.includes("dp.partners.approve") || perms.includes("dp.admin") || perms.includes("admin.full_access") || (user?.roles || []).some(r => ["Admin", "admin"].includes(r));

  const load = () => {
    setLoading(true);
    api.get(`/dp/territory-requests?status=${filter}`)
      .then(r => setRequests(r.data || []))
      .catch(e => toast.error(e.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const handleApprove = async (id) => {
    try {
      await api.post(`/dp/territory-requests/${id}/approve`);
      toast.success("Territory change approved");
      load();
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409) {
        const conflicts = d?.detail?.conflicts || [];
        toast.error(`Conflict: ${conflicts.map(c => `${c.pincode} ${c.brand_name} held by ${c.held_by?.business_name}`).join("; ")}`);
      } else {
        toast.error(typeof d?.detail === "string" ? d.detail : "Approval failed");
      }
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/dp/territory-requests/${id}/reject`, { reject_note: rejectNote[id] || "" });
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const TYPE_LABELS = { add_grants: "Add Territory", trim_pincodes: "Remove Pincodes", release_brand: "Remove Brand", close_dp: "Close Partner" };
  const STATUS_COLORS = { pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-600 border-emerald-200", rejected: "bg-red-50 text-red-600 border-red-200" };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="dp-territory-approvals-page">
      <h1 className="text-xl font-semibold text-zinc-900 mb-1">Territory Approvals</h1>
      <p className="text-xs text-zinc-400 mb-4">Approve or reject territory change requests</p>
      <div className="flex gap-1 mb-4">
        {["pending", "approved", "rejected", ""].map(s => (
          <button key={s || "all"} onClick={() => setFilter(s)} className={`px-2.5 py-1 text-[11px] font-medium rounded-sm border capitalize ${filter === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`} data-testid={`filter-${s || "all"}`}>{s || "All"}</button>
        ))}
      </div>
      {loading ? <p className="text-xs text-zinc-400 py-8 text-center">Loading...</p> : requests.length === 0 ? (
        <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No requests found.</p>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r._id} className="border border-zinc-200 rounded-sm p-4" data-testid={`req-${r._id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-zinc-800">{r.business_name}</span>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[r.status] || ""}`}>{r.status}</Badge>
                  <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[r.change_type] || r.change_type}</Badge>
                </div>
                <span className="text-[10px] text-zinc-400">{fmtDateTime(r.requested_at)}</span>
              </div>
              {r.reason && <p className="text-xs text-zinc-500 mb-1">Reason: {r.reason}</p>}
              {r.additions?.length > 0 && (
                <div className="mb-1"><span className="text-[10px] text-emerald-600 font-medium">Add: </span><span className="text-[10px] text-zinc-600">{r.additions.map(a => a.pincode).join(", ")}</span></div>
              )}
              {r.removals?.length > 0 && (
                <div className="mb-1"><span className="text-[10px] text-red-600 font-medium">Remove: </span><span className="text-[10px] text-zinc-600">{r.removals.map(a => a.pincode).join(", ")}</span></div>
              )}
              {r.close && <div className="mb-1 text-[10px] text-red-700 font-medium">CLOSE PARTNER</div>}
              {r.reject_note && <p className="text-[10px] text-red-500">Reject note: {r.reject_note}</p>}
              {canApprove && r.status === "pending" && (
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprove(r._id)} data-testid={`approve-${r._id}`}><CheckCircle className="w-3 h-3 mr-1" /> Approve</Button>
                  <Input placeholder="Reject note" className="h-7 text-[10px] max-w-[200px]" value={rejectNote[r._id] || ""} onChange={e => setRejectNote(n => ({ ...n, [r._id]: e.target.value }))} />
                  <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600" onClick={() => handleReject(r._id)} data-testid={`reject-${r._id}`}><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
